/**
 * InvitationImageStep.tsx
 *
 * Interactive invitation image builder — replaces the old PreviewStep +
 * GenerateStep for the invitation wizard.  Same layout pattern as
 * RenderImageStep (sidebar + center panel) but tailored for invitation
 * cards: portrait 9:16 aspect ratio, event detail–rich prompts, and
 * typically 2 design variants.
 *
 * Creates a wizard draft on mount (type='invitation') so the
 * generate-image endpoint has a Presentation UUID to work with.
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Skeleton, Button } from '@/components/ui';
import { extractApiError } from '@/lib/axios';
import {
  useCreateDraft,
  useUpdateDraft,
  useGenerateImage,
  usePromptAssist,
  useFinalizeRender,
} from '@/api/useWizardV2';
import { useThemes } from '@/api/usePresentationWizard';
import { AiAssistInput } from '@/pages/Dashboard/GeneratePresentation/components/AiAssistInput';
import { AddReferenceModal } from '@/pages/Dashboard/GeneratePresentation/phases/AddReferenceModal';
import { ROUTES } from '@/constants';
import type { InvitationWizardFormData } from '@/types/wizard';
import type { ReferenceImage } from '@/types/wizardV2';
import styles from './InvitationImageStep.module.css';

// ─── Types ──────────────────────────────────────────────────────────────────

interface InvitationSlot {
  index: number;
  prompt: string;
  url: string | null;
  status: 'idle' | 'generating' | 'done' | 'error';
}

interface Props {
  formData: InvitationWizardFormData;
  onBack: () => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const NUM_INVITATION_VARIANTS = 2;

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildInvitationPrompt(
  index: number,
  total: number,
  formData: InvitationWizardFormData,
  themeName: string,
  primaryColor: string,
  accentColor: string,
): string {
  const styles = [
    'elegant and sophisticated with refined typography layout, ornamental accents and luxurious textures',
    'modern and bold with clean geometric composition, vibrant gradients and striking contrast',
  ];
  const style = styles[index % styles.length];

  let prompt = `Photorealistic high-end invitation card design, portrait orientation (9:16 aspect ratio). `;
  prompt += `Style: ${style}. `;
  prompt += `Event: "${formData.event_name}". `;
  prompt += `Type: ${formData.event_type}. `;
  prompt += `Theme: ${themeName}. `;
  prompt += `Primary color: ${primaryColor}, accent color: ${accentColor}. `;

  if (formData.venue) {
    prompt += `Venue: ${formData.venue}. `;
  }
  if (formData.dress_code) {
    prompt += `Dress code: ${formData.dress_code}. `;
  }
  if (formData.special_notes.trim()) {
    prompt += `Special notes: ${formData.special_notes.trim().slice(0, 300)}. `;
  }

  prompt += `Design ${index + 1} of ${total}. `;
  prompt += `CRITICAL: This is an invitation card design. Include elegant layout placeholders for event details. `;
  prompt += `NO real readable text — use abstract calligraphic strokes or blurred placeholder text instead of actual words. `;
  prompt += `Photorealistic print-quality design, professional event invitation.`;

  return prompt;
}

// ─── Component ──────────────────────────────────────────────────────────────

export const InvitationImageStep = ({ formData, onBack }: Props) => {
  const navigate = useNavigate();

  // ── Resolve theme data ──────────────────────────────────────────────────

  const { data: themes } = useThemes();
  const selectedTheme = themes?.find((t) => formData.theme_slugs.includes(t.slug));
  const themeName = selectedTheme?.name ?? 'Elegant';
  const primaryColor = selectedTheme?.color_suggestion?.primary ?? '#0C1B33';
  const accentColor = selectedTheme?.color_suggestion?.accent ?? '#C9A84C';

  // ── API mutations ──────────────────────────────────────────────────────

  const createDraft = useCreateDraft();
  const updateDraft = useUpdateDraft();
  const generateImage = useGenerateImage();
  const promptAssist = usePromptAssist();
  const finalizeRender = useFinalizeRender();

  // ── Draft presentation ID ─────────────────────────────────────────────

  const [draftId, setDraftId] = useState<string | null>(null);
  const draftCreatedRef = useRef(false);

  // ── Invitation slots ──────────────────────────────────────────────────

  const [slots, setSlots] = useState<InvitationSlot[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // ── Reference images ──────────────────────────────────────────────────

  const [referenceImages, setReferenceImages] = useState<ReferenceImage[]>([]);
  const [slotReferenceIds, setSlotReferenceIds] = useState<Record<number, string[]>>({});
  const [refModalOpen, setRefModalOpen] = useState(false);

  // ── UI state ──────────────────────────────────────────────────────────

  const [defaultProvider, setDefaultProvider] = useState<'gemini' | 'openai'>('gemini');
  const [slotProviders, setSlotProviders] = useState<Record<number, 'gemini' | 'openai'>>({});
  const [assistError, setAssistError] = useState<string | null>(null);

  // ── Initialise slots on mount ─────────────────────────────────────────

  useEffect(() => {
    if (slots.length > 0) return;

    const initialSlots: InvitationSlot[] = Array.from(
      { length: NUM_INVITATION_VARIANTS },
      (_, i) => ({
        index: i,
        prompt: buildInvitationPrompt(i, NUM_INVITATION_VARIANTS, formData, themeName, primaryColor, accentColor),
        url: null,
        status: 'idle' as const,
      }),
    );
    setSlots(initialSlots);
  }, [formData, themeName, primaryColor, accentColor, slots.length]);

  // ── Create draft on mount ─────────────────────────────────────────────

  useEffect(() => {
    if (draftCreatedRef.current || draftId) return;
    draftCreatedRef.current = true;

    createDraft.mutate(
      {
        type: 'invitation',
        city: formData.city,
        theme_slugs: formData.theme_slugs,
        event_name: formData.event_name,
        prompt: formData.special_notes,
        logo_url: formData.logo_url,
        context_data: {
          eventType: formData.event_type,
          eventDate: formData.event_date,
          eventTime: formData.time,
          location: formData.venue,
          dressCode: formData.dress_code,
          specialNotes: formData.special_notes,
          logo_url: formData.logo_url,
          city: formData.city,
          theme_slugs: formData.theme_slugs,
        },
      } as Record<string, unknown>,
      {
        onSuccess: (data) => {
          setDraftId(data.uuid);
        },
        onError: (err) => {
          toast.error(extractApiError(err, 'Failed to create invitation draft.'), { duration: 8_000 });
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-load logo as reference ───────────────────────────────────────

  const logoLoadedRef = useRef(false);

  useEffect(() => {
    if (logoLoadedRef.current || !formData.logo_url) return;
    logoLoadedRef.current = true;

    const logoRef: ReferenceImage = {
      id: 'logo',
      url: formData.logo_url,
      source: 'logo' as const,
    };

    setReferenceImages((prev) => {
      if (prev.some((r) => r.id === 'logo')) return prev;
      return [...prev, logoRef];
    });

    setSlotReferenceIds((prev) => {
      const updated = { ...prev };
      for (let i = 0; i < NUM_INVITATION_VARIANTS; i++) {
        const existing = new Set(updated[i] ?? []);
        existing.add('logo');
        updated[i] = Array.from(existing);
      }
      return updated;
    });
  }, [formData.logo_url]);

  // ── Derived state ─────────────────────────────────────────────────────

  const selectedSlot = slots[selectedIndex] ?? null;
  const activeProvider = slotProviders[selectedIndex] ?? defaultProvider;
  const activeRefIds = slotReferenceIds[selectedIndex] ?? [];
  const activeRefImages = referenceImages.filter((r) => activeRefIds.includes(r.id));

  const doneCount = slots.filter((s) => s.status === 'done' && s.url).length;
  const totalCount = slots.length;

  // ── Generate single invitation ────────────────────────────────────────

  const generateSlot = useCallback(
    async (index: number): Promise<void> => {
      if (!draftId) {
        toast.error('Draft not ready yet. Please wait.');
        return;
      }

      const slot = slots[index];
      if (!slot || !slot.prompt.trim()) return;

      const provider = slotProviders[index] ?? defaultProvider;
      const refIds = slotReferenceIds[index] ?? [];
      const refUrls = refIds
        .map((id) => referenceImages.find((r) => r.id === id)?.url)
        .filter((u): u is string => !!u);

      setSlots((prev) =>
        prev.map((s, i) => (i === index ? { ...s, status: 'generating' as const } : s)),
      );

      try {
        const result = await generateImage.mutateAsync({
          id: draftId,
          slotKey: `invitation-${index}`,
          prompt: slot.prompt,
          provider,
          referenceImageUrls: refUrls,
        });

        setSlots((prev) =>
          prev.map((s, i) =>
            i === index ? { ...s, url: result.url, status: 'done' as const } : s,
          ),
        );

        setReferenceImages((prev) => {
          const genRefId = `generated-invitation-${index}`;
          if (prev.some((r) => r.id === genRefId)) {
            return prev.map((r) => (r.id === genRefId ? { ...r, url: result.url } : r));
          }
          return [...prev, { id: genRefId, url: result.url, source: 'generated' as const }];
        });
      } catch (err) {
        setSlots((prev) =>
          prev.map((s, i) => (i === index ? { ...s, status: 'error' as const } : s)),
        );
        toast.error(extractApiError(err, 'Error generating invitation. Try again.'), {
          duration: 8_000,
        });
      }
    },
    [draftId, slots, slotProviders, defaultProvider, slotReferenceIds, referenceImages, generateImage],
  );

  // ── Generate all ──────────────────────────────────────────────────────

  const handleGenerateAll = useCallback(() => {
    if (!draftId) {
      toast.error('Draft not ready yet. Please wait.');
      return;
    }

    const pending = slots
      .map((s, i) => (s.status === 'idle' || s.status === 'error' ? i : -1))
      .filter((i) => i >= 0);

    if (pending.length === 0) {
      toast('All invitations already generated.', { duration: 3_000 });
      return;
    }

    void Promise.allSettled(pending.map((i) => generateSlot(i))).then((results) => {
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed === 0) {
        toast.success('All invitations generated!', { duration: 3_000 });
      } else {
        toast.error(`${failed} invitation(s) failed. Check each one.`, { duration: 8_000 });
      }
    });
  }, [draftId, slots, generateSlot]);

  // ── Prompt change ─────────────────────────────────────────────────────

  const handlePromptChange = useCallback(
    (value: string) => {
      setSlots((prev) =>
        prev.map((s, i) => (i === selectedIndex ? { ...s, prompt: value } : s)),
      );
    },
    [selectedIndex],
  );

  // ── Provider change ───────────────────────────────────────────────────

  const handleProviderChange = useCallback(
    (provider: 'gemini' | 'openai') => {
      setSlotProviders((prev) => ({ ...prev, [selectedIndex]: provider }));
    },
    [selectedIndex],
  );

  // ── AI Assist ─────────────────────────────────────────────────────────

  const handlePromptAssist = useCallback(
    async (message: string): Promise<void> => {
      if (!draftId) return;

      setAssistError(null);

      try {
        const currentPrompt = slots[selectedIndex]?.prompt ?? '';
        const result = await promptAssist.mutateAsync({
          id: draftId,
          instruction: message,
          currentPrompt,
          slideContext: {
            component: 'invitation',
            section: 'Invitation',
            title: formData.event_name,
          },
        });

        setSlots((prev) =>
          prev.map((s, i) => (i === selectedIndex ? { ...s, prompt: result.prompt } : s)),
        );
      } catch {
        const errorMsg = 'Could not improve the prompt. Try again.';
        setAssistError(errorMsg);
        throw new Error(errorMsg);
      }
    },
    [draftId, selectedIndex, slots, promptAssist, formData.event_name],
  );

  // ── Reference image management ────────────────────────────────────────

  const handleReferenceSelect = useCallback(
    (images: ReferenceImage[]) => {
      setReferenceImages((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const newRefs = images.filter((img) => !existingIds.has(img.id));
        return [...prev, ...newRefs];
      });

      setSlotReferenceIds((prev) => {
        const existing = new Set(prev[selectedIndex] ?? []);
        images.forEach((img) => existing.add(img.id));
        return { ...prev, [selectedIndex]: Array.from(existing) };
      });
    },
    [selectedIndex],
  );

  const handleRemoveReference = useCallback(
    (id: string) => {
      setSlotReferenceIds((prev) => ({
        ...prev,
        [selectedIndex]: (prev[selectedIndex] ?? []).filter((rid) => rid !== id),
      }));
    },
    [selectedIndex],
  );

  // ── Finalize ──────────────────────────────────────────────────────────

  const [isFinalizing, setIsFinalizing] = useState(false);

  const handleFinalize = useCallback(async () => {
    if (!draftId) return;

    const generatedUrls = slots.filter((s) => s.url).map((s) => s.url as string);
    if (generatedUrls.length === 0) {
      toast.error('Generate at least one invitation before finishing.');
      return;
    }

    setIsFinalizing(true);

    try {
      await updateDraft.mutateAsync({
        id: draftId,
        data: {
          generated_images: generatedUrls,
          reference_images: referenceImages.map((r) => r.url).filter(Boolean),
        },
      });

      await finalizeRender.mutateAsync({ id: draftId });

      toast.success('Invitations saved!', { duration: 3_000 });
      navigate(ROUTES.dashboardInvitations);
    } catch (err) {
      toast.error(extractApiError(err, 'Failed to save invitations.'), { duration: 8_000 });
    } finally {
      setIsFinalizing(false);
    }
  }, [draftId, slots, referenceImages, updateDraft, finalizeRender, navigate]);

  // ── Derived generated images for AddReferenceModal ────────────────────

  const generatedImagesForModal = useMemo(
    () =>
      slots
        .filter((s) => s.status === 'done' && s.url)
        .map((s) => ({ url: s.url as string, slideIndex: s.index })),
    [slots],
  );

  // ── Render ────────────────────────────────────────────────────────────

  const isDraftLoading = createDraft.isPending;

  return (
    <div className={styles.container}>
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className={styles.toolbar} role="toolbar" aria-label="Invitation generation options">
        <button
          type="button"
          className={styles.backButton}
          onClick={onBack}
          aria-label="Go back to details"
        >
          ← Back
        </button>

        <Button
          variant="secondary"
          size="sm"
          onClick={handleGenerateAll}
          disabled={isDraftLoading || totalCount === 0}
          aria-label="Generate all pending invitations"
        >
          Generate all
        </Button>

        <div className={styles.toolbarSpacer} />

        <fieldset className={styles.providerFieldset}>
          <legend className={styles.providerLegend}>Default provider</legend>
          <label className={styles.providerLabel}>
            <input
              type="radio"
              name="default-provider"
              value="gemini"
              checked={defaultProvider === 'gemini'}
              onChange={() => setDefaultProvider('gemini')}
              className={styles.providerRadio}
            />
            Gemini
          </label>
          <label className={styles.providerLabel}>
            <input
              type="radio"
              name="default-provider"
              value="openai"
              checked={defaultProvider === 'openai'}
              onChange={() => setDefaultProvider('openai')}
              className={styles.providerRadio}
            />
            OpenAI
          </label>
        </fieldset>

        <span className={styles.slotCounter} aria-live="polite" aria-atomic>
          {isDraftLoading ? 'Creating draft...' : `${doneCount}/${totalCount} designs`}
        </span>
      </div>

      {/* ── Editor area ──────────────────────────────────────────────────── */}
      <div className={styles.editorArea}>
        {/* ── Left sidebar ─────────────────────────────────────────────── */}
        <div className={styles.sidebar} aria-label="Invitation designs">
          <div className={styles.sidebarHeader}>
            <h2 className={styles.sidebarHeaderTitle}>Designs</h2>
          </div>

          {isDraftLoading ? (
            <div className={styles.sidebarSkeletonList} aria-label="Loading..." aria-busy>
              {Array.from({ length: NUM_INVITATION_VARIANTS }, (_, i) => (
                <Skeleton key={i} variant="rect" height={120} />
              ))}
            </div>
          ) : (
            <div className={styles.slideList} role="list">
              {slots.map((slot, i) => {
                const isActive = selectedIndex === i;
                return (
                  <div
                    key={i}
                    role="listitem"
                    className={[
                      styles.invitationCard,
                      isActive ? styles.invitationCardSelected : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setSelectedIndex(i)}
                    aria-label={`Design ${i + 1}`}
                    aria-selected={isActive}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedIndex(i);
                      }
                    }}
                  >
                    {slot.url ? (
                      <img
                        src={slot.url}
                        alt={`Design ${i + 1}`}
                        className={styles.invitationThumb}
                      />
                    ) : (
                      <div className={styles.invitationThumbPlaceholder}>
                        <span className={styles.invitationThumbNumber}>{i + 1}</span>
                      </div>
                    )}

                    <span
                      className={[
                        styles.statusBadge,
                        styles[`statusBadge--${slot.status}`],
                      ].join(' ')}
                      aria-label={`Status: ${slot.status}`}
                    >
                      {slot.status === 'generating' && (
                        <span className={styles.statusDot} aria-hidden />
                      )}
                      {slot.status === 'done' && <span aria-hidden>&#10003;</span>}
                      {slot.status === 'error' && <span aria-hidden>!</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Center panel ──────────────────────────────────────────────── */}
        <div className={styles.centerPanel} aria-label="Invitation generation panel">
          {selectedSlot === null ? (
            <div className={styles.emptyCenter}>
              <p>Select a design from the left.</p>
            </div>
          ) : (
            <div className={styles.panelBody}>
              {/* ── Header ──────────────────────────────────────────── */}
              <div className={styles.panelHeader}>
                <p className={styles.panelSlideTitle}>
                  Design {selectedIndex + 1} of {totalCount}
                </p>
                <p className={styles.panelSlideInfo}>
                  {formData.event_type} · {formData.event_name}
                </p>
              </div>

              {/* ── Nav arrows + controls ──────────────────────────── */}
              <div className={styles.navControlsRow}>
                <button
                  type="button"
                  className={styles.navArrowTall}
                  disabled={selectedIndex <= 0}
                  onClick={() => setSelectedIndex((p) => Math.max(0, p - 1))}
                  aria-label="Previous design"
                >
                  ‹
                </button>

                <div className={styles.controlsCol}>
                  {/* ── Prompt textarea ──────────────────────────── */}
                  <div className={styles.promptSection}>
                    <label className={styles.sectionLabel} htmlFor="invitation-prompt">
                      Image prompt
                    </label>
                    <textarea
                      id="invitation-prompt"
                      className={styles.promptTextarea}
                      value={selectedSlot.prompt}
                      onChange={(e) => handlePromptChange(e.target.value)}
                      rows={5}
                      disabled={selectedSlot.status === 'generating'}
                      aria-label="Edit invitation prompt"
                    />
                  </div>

                  {/* ── Reference images strip ──────────────────── */}
                  <div className={styles.refsSection}>
                    <span className={styles.sectionLabel}>References</span>
                    <div className={styles.refsRow}>
                      {activeRefImages.map((ref) => (
                        <div key={ref.id} className={styles.refChip}>
                          <img
                            src={ref.url}
                            alt={ref.source}
                            className={styles.refChipImg}
                          />
                          <button
                            type="button"
                            className={styles.refChipRemove}
                            onClick={() => handleRemoveReference(ref.id)}
                            aria-label={`Remove reference ${ref.source}`}
                          >
                            &#10005;
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        className={styles.addRefButton}
                        onClick={() => setRefModalOpen(true)}
                        aria-label="Add reference image"
                      >
                        + Add reference
                      </button>
                    </div>
                  </div>

                  {/* ── AI Assist ────────────────────────────────── */}
                  <div className={styles.assistSection}>
                    <span className={styles.sectionLabel}>Adjust prompt</span>
                    <AiAssistInput
                      placeholder="Make it more luxurious, add floral elements, change the color scheme..."
                      onSubmit={handlePromptAssist}
                      isLoading={promptAssist.isPending}
                      error={assistError}
                      onClearError={() => setAssistError(null)}
                    />
                  </div>

                  {/* ── Provider toggle ──────────────────────────── */}
                  <div className={styles.optionsRow}>
                    <fieldset className={styles.providerFieldset}>
                      <legend className={styles.providerLegend}>Provider</legend>
                      <label className={styles.providerLabel}>
                        <input
                          type="radio"
                          name={`slot-provider-${selectedIndex}`}
                          value="gemini"
                          checked={activeProvider === 'gemini'}
                          onChange={() => handleProviderChange('gemini')}
                          className={styles.providerRadio}
                        />
                        Gemini
                      </label>
                      <label className={styles.providerLabel}>
                        <input
                          type="radio"
                          name={`slot-provider-${selectedIndex}`}
                          value="openai"
                          checked={activeProvider === 'openai'}
                          onChange={() => handleProviderChange('openai')}
                          className={styles.providerRadio}
                        />
                        OpenAI
                      </label>
                    </fieldset>
                  </div>

                  {/* ── Generate / Regenerate ────────────────────── */}
                  <div className={styles.generateRow}>
                    <Button
                      variant="primary"
                      size="md"
                      onClick={() => void generateSlot(selectedIndex)}
                      disabled={
                        !draftId ||
                        !selectedSlot.prompt.trim() ||
                        selectedSlot.status === 'generating'
                      }
                      aria-label={selectedSlot.url ? 'Regenerate design' : 'Generate design'}
                    >
                      {selectedSlot.status === 'generating'
                        ? 'Generating...'
                        : selectedSlot.url
                          ? 'Regenerate'
                          : 'Generate'}
                    </Button>
                  </div>
                </div>

                <button
                  type="button"
                  className={styles.navArrowTall}
                  disabled={selectedIndex >= totalCount - 1}
                  onClick={() => setSelectedIndex((p) => Math.min(totalCount - 1, p + 1))}
                  aria-label="Next design"
                >
                  ›
                </button>
              </div>

              {/* ── Preview area ──────────────────────────────────── */}
              <div className={styles.previewSection}>
                <span className={styles.sectionLabel}>Preview</span>
                <div className={styles.previewBox} aria-label="Design preview">
                  {selectedSlot.status === 'generating' ? (
                    <Skeleton variant="rect" height={360} />
                  ) : selectedSlot.url ? (
                    <img
                      src={selectedSlot.url}
                      alt={`Design ${selectedIndex + 1}`}
                      className={styles.previewImg}
                    />
                  ) : (
                    <div className={styles.previewEmpty}>
                      <p>Generate the design to see a preview.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <div className={styles.footerRow}>
        <Button
          variant="primary"
          size="md"
          onClick={handleFinalize}
          disabled={doneCount === 0 || isFinalizing}
          aria-label="Finish and save invitations"
        >
          {isFinalizing ? 'Saving...' : `Finish (${doneCount}/${totalCount} designs) →`}
        </Button>
      </div>

      {/* ── AddReferenceModal ─────────────────────────────────────────────── */}
      <AddReferenceModal
        isOpen={refModalOpen}
        onClose={() => setRefModalOpen(false)}
        onSelect={handleReferenceSelect}
        logoUrl={formData.logo_url}
        generatedImages={generatedImagesForModal}
      />
    </div>
  );
};
